// src/components/ScrollSync.tsx
import React, { useCallback, useRef } from "react";

// src/hooks/useScrollSyncContext.ts
import { createContext, useContext } from "react";
var ScrollSyncContext = createContext(void 0);
var useScrollSyncContext = () => {
  const context = useContext(ScrollSyncContext);
  if (!context) {
    throw new Error(
      "useScrollSyncContext must be used within a ScrollSyncProvider"
    );
  }
  return context;
};

// src/components/ScrollSync.tsx
import { jsx } from "react/jsx-runtime";
var ScrollSync = ({
  children,
  enabled = true,
  horizontal = true,
  onSync,
  proportional = true,
  vertical = true
}) => {
  const panesRef = useRef({});
  const findPane = useCallback((node, group) => {
    if (!panesRef.current[group]) {
      return false;
    }
    return panesRef.current[group].find((pane) => pane === node);
  }, []);
  const syncScrollPosition = useCallback(
    (scrolledPane, pane) => {
      const {
        clientHeight,
        clientWidth,
        scrollHeight,
        scrollLeft,
        scrollTop,
        scrollWidth
      } = scrolledPane;
      const scrollTopOffset = scrollHeight - clientHeight;
      const scrollLeftOffset = scrollWidth - clientWidth;
      const paneHeight = pane.scrollHeight - clientHeight;
      const paneWidth = pane.scrollWidth - clientWidth;
      if (vertical && scrollTopOffset > 0) {
        pane.scrollTop = proportional ? paneHeight * scrollTop / scrollTopOffset : scrollTop;
      }
      if (horizontal && scrollLeftOffset > 0) {
        pane.scrollLeft = proportional ? paneWidth * scrollLeft / scrollLeftOffset : scrollLeft;
      }
    },
    [proportional, vertical, horizontal]
  );
  const removeEvents = useCallback((node) => {
    node.onscroll = null;
  }, []);
  const addEvents = useCallback(
    (node, groups) => {
      node.onscroll = () => {
        if (!enabled) return;
        window.requestAnimationFrame(() => {
          groups.forEach((group) => {
            panesRef.current[group]?.forEach((pane) => {
              if (node !== pane) {
                removeEvents(pane);
                syncScrollPosition(node, pane);
                window.requestAnimationFrame(() => {
                  const paneGroups = Object.keys(panesRef.current).filter(
                    (paneGroup) => panesRef.current[paneGroup].includes(pane)
                  );
                  addEvents(pane, paneGroups);
                });
              }
            });
          });
        });
        if (onSync) {
          onSync(node);
        }
      };
    },
    [onSync, removeEvents, syncScrollPosition, enabled]
  );
  const registerPane = useCallback(
    (node, groups) => {
      groups.forEach((group) => {
        if (!panesRef.current[group]) {
          panesRef.current[group] = [];
        }
        if (!findPane(node, group)) {
          if (panesRef.current[group].length > 0) {
            syncScrollPosition(panesRef.current[group][0], node);
          }
          panesRef.current[group].push(node);
        }
      });
      addEvents(node, groups);
    },
    [findPane, syncScrollPosition, addEvents]
  );
  const unregisterPane = useCallback(
    (node, groups) => {
      groups.forEach((group) => {
        if (findPane(node, group)) {
          removeEvents(node);
          const index = panesRef.current[group].indexOf(node);
          if (index !== -1) {
            panesRef.current[group].splice(index, 1);
          }
        }
      });
    },
    [findPane, removeEvents]
  );
  return /* @__PURE__ */ jsx(ScrollSyncContext.Provider, { value: { registerPane, unregisterPane }, children: React.Children.only(children) });
};

// src/components/ScrollSyncPane.tsx
import {
  cloneElement,
  useCallback as useCallback2,
  useEffect,
  useRef as useRef2
} from "react";
var castArray = (groups) => Array.isArray(groups) ? groups : [groups];
var ScrollSyncPane = ({
  attachTo,
  children,
  enabled = true,
  group = "default",
  innerRef
}) => {
  const context = useScrollSyncContext();
  const childRef = useRef2(null);
  const nodeRef = useRef2(null);
  const updateNode = useCallback2(() => {
    if (attachTo) {
      nodeRef.current = typeof attachTo === "function" ? null : attachTo.current;
    } else {
      nodeRef.current = childRef.current;
    }
  }, [attachTo]);
  useEffect(() => {
    updateNode();
    if (enabled && nodeRef.current) {
      context.registerPane(nodeRef.current, castArray(group));
    }
    return () => {
      if (enabled && nodeRef.current) {
        context.unregisterPane(nodeRef.current, castArray(group));
      }
    };
  }, [context, enabled, group, attachTo, updateNode]);
  if (attachTo) {
    return children;
  }
  return cloneElement(children, {
    ref: (node) => {
      childRef.current = node;
      if (typeof innerRef === "function") {
        innerRef(node);
      } else if (innerRef && node) {
        innerRef.current = node;
      }
    }
  });
};
export {
  ScrollSync,
  ScrollSyncPane
};
//# sourceMappingURL=index.js.map